'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { MessageCircle, Eye, Heart, Image as ImageIcon, PenSquare } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useTheme } from '@/contexts/ThemeContext'
import { maskName } from '@/utils/privacy'

interface Post {
  id: string
  user_id: string
  title: string
  content: string
  rating: number
  diving_spot: string | null
  diving_pool: string | null
  diving_date: string | null
  created_at: string
  user_profiles: {
    name: string
    profile_image: string
  }
  review_images: {
    image_url: string
    display_order: number
  }[]
  view_count?: number
  comment_count?: number
  like_count?: number
}

export default function FreeBoardPage() {
  const { theme } = useTheme()
  const [posts, setPosts] = useState<Post[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const isDark = theme === 'dark'

  useEffect(() => {
    loadPosts()
  }, [])

  const loadPosts = async () => {
    try {
      const supabase = getSupabaseClient()

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          user_profiles!reviews_user_id_fkey (
            name,
            profile_image
          ),
          review_images (
            image_url,
            display_order
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      // 각 게시글의 실제 좋아요/댓글 수 조회
      const postsWithCounts = await Promise.all(
        (data || []).map(async (post) => {
          const [likeResult, commentResult] = await Promise.all([
            supabase
              .from('review_likes')
              .select('*', { count: 'exact', head: true })
              .eq('review_id', post.id),
            supabase
              .from('review_comments')
              .select('*', { count: 'exact', head: true })
              .eq('review_id', post.id)
          ])

          return {
            ...post,
            like_count: likeResult.count ?? 0,
            comment_count: commentResult.count ?? 0
          }
        })
      )

      setPosts(postsWithCounts)
    } catch (error) {
      console.error('게시글 로드 실패:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInMinutes < 1) return '방금 전'
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`
    if (diffInHours < 24) return `${diffInHours}시간 전`
    if (diffInDays < 7) return `${diffInDays}일 전`
    return date.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-2xl mx-auto">
        {/* 헤더 */}
        <div className={`sub-sticky z-20 ${isDark ? 'bg-gray-900' : 'bg-white'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link
                  href="/community"
                  className={`text-sm ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  ←
                </Link>
                <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  자유게시판
                </h1>
                <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {posts.length}
                </span>
              </div>
              <Link
                href="/community/free-board/new"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <PenSquare className="w-4 h-4" />
                글쓰기
              </Link>
            </div>
          </div>
        </div>

        {/* 게시글 목록 */}
        <div className="pb-20">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                로딩 중...
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div className={`mx-4 mt-4 rounded-xl p-8 text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                아직 작성된 글이 없습니다.
                <br />
                첫 번째 글을 작성해보세요!
              </p>
              <Link
                href="/community/free-board/new"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <PenSquare className="w-4 h-4" />
                글쓰기
              </Link>
            </div>
          ) : (
            <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {posts.map((post) => (
                <Link
                  key={post.id}
                  href={`/community/free-board/${post.id}`}
                  className={`block px-4 py-3.5 transition-colors ${
                    isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* 상단: 제목 + 이미지 아이콘 */}
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className={`flex-1 text-[15px] font-medium leading-snug line-clamp-2 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {post.title}
                    </h3>
                    {post.review_images && post.review_images.length > 0 && (
                      <ImageIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    )}
                  </div>

                  {/* 본문 미리보기 (1줄) */}
                  <p className={`text-sm line-clamp-1 mb-2.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {post.content}
                  </p>

                  {/* 하단: 작성자 | 시간 | 조회 | 좋아요 | 댓글 */}
                  <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {maskName(post.user_profiles?.name)}
                    </span>
                    <span>·</span>
                    <span>{formatDate(post.created_at)}</span>
                    <span>·</span>
                    <span className="flex items-center gap-0.5">
                      <Eye className="w-3.5 h-3.5" />
                      {post.view_count ?? 0}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Heart className="w-3.5 h-3.5" />
                      {post.like_count ?? 0}
                    </span>
                    <span className={`flex items-center gap-0.5 ${(post.comment_count ?? 0) > 0 ? (isDark ? 'text-blue-400' : 'text-blue-600') : ''}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                      {post.comment_count ?? 0}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
